import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useHrAccess } from "../../hooks/useHrAccess";
import { fetchLeaveBalanceLogs, fetchLeaveTypes } from "../../store/hr/hrSlice";
import { HrLeaveBalanceLog } from "../../types/HrTypes";
import {
  AdminPageHeader,
  AdminPageShell,
  AdminSoftInput,
  AdminSoftSelect,
  AdminTableCard,
  AdminTableState,
  adminTableCell,
  adminTableHead,
} from "../../components/admin/AdminPageShell";
import { CustomDatePicker } from "../../components/common/DatePickers";

function formatWhen(iso?: string) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fieldLabel(field: string) {
  if (field === "remaining") return "Remaining";
  if (field === "allocated") return "Allocated";
  if (field === "used") return "Used";
  if (field === "removed") return "Removed";
  return field;
}

function sourceLabel(source: string) {
  if (source === "attendance") return "Attendance";
  if (source === "leaves_assign") return "Leaves assign";
  if (source === "opening_balances") return "Opening balances";
  if (source === "remove_balance") return "Remove type";
  if (source === "set_balance") return "Balance edit";
  return source;
}

const LeaveBalanceLogs = () => {
  const dispatch = useAppDispatch();
  const { isSuperAdmin } = useHrAccess();
  const { leaveTypes, error } = useAppSelector((state) => state.hr);
  const [rows, setRows] = useState<HrLeaveBalanceLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [empId, setEmpId] = useState("");
  const [leaveTypeId, setLeaveTypeId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const limit = 50;

  useEffect(() => {
    if (!isSuperAdmin) return;
    dispatch(fetchLeaveTypes());
  }, [dispatch, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    setLoading(true);
    dispatch(
      fetchLeaveBalanceLogs({
        emp_id: empId ? Number(empId) : undefined,
        leave_type_id: leaveTypeId ? Number(leaveTypeId) : undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        limit,
      }),
    )
      .then((result) => {
        if (cancelled) return;
        if (fetchLeaveBalanceLogs.fulfilled.match(result)) {
          setRows(result.payload.rows);
          setTotal(result.payload.total);
        } else {
          setRows([]);
          setTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, isSuperAdmin, empId, leaveTypeId, from, to, page]);

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard/employee/leaves" replace />;
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <>
      <Helmet>
        <title>Leave balance logs</title>
      </Helmet>
      <AdminPageShell accent="#345952">
        <AdminPageHeader
          title="Leave balance logs"
          subtitle="Super Admin only. Who changed leave balances, from → to, when, and for which employee."
        />
        {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}

        <div className="mb-4 flex flex-wrap items-end gap-3">
          <label className="text-xs text-[#8A8A85]">
            Employee ID
            <AdminSoftInput
              className="mt-1 block w-28"
              value={empId}
              onChange={(e) => {
                setPage(1);
                setEmpId(e.target.value.replace(/\D/g, ""));
              }}
              placeholder="e.g. 12"
            />
          </label>
          <label className="text-xs text-[#8A8A85]">
            Leave type
            <AdminSoftSelect
              className="mt-1 block min-w-[160px]"
              value={leaveTypeId}
              onChange={(e) => {
                setPage(1);
                setLeaveTypeId(e.target.value);
              }}
            >
              <option value="">All types</option>
              {leaveTypes.map((t) => (
                <option key={t.leave_type_id} value={t.leave_type_id}>
                  {t.name} ({t.code})
                </option>
              ))}
            </AdminSoftSelect>
          </label>
          <CustomDatePicker
            label="From"
            value={from}
            onChange={(v) => {
              setPage(1);
              setFrom(v);
            }}
          />
          <CustomDatePicker
            label="To"
            value={to}
            onChange={(v) => {
              setPage(1);
              setTo(v);
            }}
          />
        </div>

        <AdminTableCard>
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={adminTableHead}>When</th>
                <th className={adminTableHead}>Employee</th>
                <th className={adminTableHead}>Leave type</th>
                <th className={adminTableHead}>Field</th>
                <th className={adminTableHead}>From</th>
                <th className={adminTableHead}>To</th>
                <th className={adminTableHead}>Edited by</th>
                <th className={adminTableHead}>Source</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <AdminTableState colSpan={8} variant="loading" message="Loading logs…" />
              ) : rows.length === 0 ? (
                <AdminTableState colSpan={8} variant="empty" message="No leave balance edits yet" />
              ) : (
                rows.map((row) => (
                  <tr key={row.log_id} className="border-b border-[#F0F0EC]">
                    <td className={adminTableCell}>{formatWhen(row.edited_at)}</td>
                    <td className={adminTableCell}>
                      {row.Employee?.emp_name || `Emp #${row.emp_id}`}
                      <span className="ml-1 text-[11px] text-[#8A8A85]">#{row.emp_id}</span>
                    </td>
                    <td className={adminTableCell}>
                      {row.leaveType?.name || row.leaveType?.code || `Type #${row.leave_type_id}`}
                      {row.year ? (
                        <span className="ml-1 text-[11px] text-[#8A8A85]">{row.year}</span>
                      ) : null}
                    </td>
                    <td className={`${adminTableCell} capitalize`}>{fieldLabel(row.field)}</td>
                    <td className={adminTableCell}>{row.old_value}</td>
                    <td className={`${adminTableCell} font-semibold text-[#345952]`}>
                      {row.new_value}
                    </td>
                    <td className={adminTableCell}>{row.edited_by || "—"}</td>
                    <td className={adminTableCell}>{sourceLabel(row.source)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AdminTableCard>

        {totalPages > 1 ? (
          <div className="mt-3 flex items-center justify-between text-xs text-[#8A8A85]">
            <span>
              {total} change{total === 1 ? "" : "s"} · page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-[#E8E8E4] bg-white px-3 py-1.5 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="rounded-lg border border-[#E8E8E4] bg-white px-3 py-1.5 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </AdminPageShell>
    </>
  );
};

export default LeaveBalanceLogs;
